import React, { PureComponent } from 'react';
import SelectField from './select-field';
import TextField from './text-field';
import NumberField from './number-field';
import SelectDropDownField from './select-dropdown-field';
import Address from './address';
import BackButton from './back-button';
import ActionArea from './action-area';
import { validNameKey, handleOnPasteForNameField } from '../lib/valid-key';
import Spinner from './spinner';

const businessTypes = [
  'Sole Proprietorship',
  'Partnership',
  'Limited Liability Company',
  'S-Corporation',
  'Corporation (Privately Held)',
  'Corporation (Publicly Traded)',
  'Other'
];

class PracticeInformation extends PureComponent {

  constructor(props) {
    super(props);
  }

  state = {
    disableAction: false
  }

  handleNameChange = (e) => {
    const { enterPracticeName } = this.props.actions;
    const { target: { value } } = e;
    enterPracticeName(value);
  };

  handleTypeChange = (value) => {
    const { selectPracticeType } = this.props.actions;
    selectPracticeType(value);
  }

  handleLegalNameChange = (e) => {
    const { value } = e.target;
    const { enterFieldData } = this.props.actions;
    enterFieldData('legalName', value, {
      isRequired: true,
      fieldDisplayName: 'Legal Business Name'
    });
  };

  handleBusinessTINBlur = (e) => {
    const { value } = e.target;
    const { enterFieldData } = this.props.actions;
    enterFieldData('businessTIN', value, {
      isRequired: true,
      fieldDisplayName: 'Business TIN',
      validator: (value) => {
        if (value.length !== 9) {
          return 'Business TIN should have 9 digits.';
        }
        return null;
      }
    });
  };

  handleBusinessTINChange = (e) => {
    const { value } = e.target;
    const { enterFieldData } = this.props.actions;
    enterFieldData('businessTIN', value, {
      isRequired: false,
      fieldDisplayName: 'Business TIN'
    });
  };

  handleBusinessTypeChange = (e) => {
    const { value } = e.target;
    const { enterFieldData } = this.props.actions;
    enterFieldData('businessType', value, {
      isRequired: true,
      fieldDisplayName: 'Business type'
    });
  }

  handleSubmit = () => {
    this.setState({
      disableAction: true
    }, () => {
      const { submitBusinessInfo } = this.props.actions;
      submitBusinessInfo(this.props.practice)
        .then((result) => {
          if (result && result.capAlreadyReview) { return; }
          
          const route = this.props.isInReview
            ? '/patientsolutions/enroll/review'
            : '/patientsolutions/enroll/payment-plans';

          this.props.history.push(route);
        })
        .catch(({ response }) => {
          this.setState({ disableAction: false });
          const { status, data } = response;

          if (status && status !== 400) {
            window.location.href = '/patientsolutions/enroll/error';
          }

          if (data && data.redirectTo) {
            window.location.href = data.redirectTo;
          }
        });
      });
  }

  hasValidations = () => {
    const { errors } = this.props;
    return errors.practiceName ||
      errors.type ||
      errors.businessType ||
      errors.businessTIN ||
      errors.city ||
      errors.state ||
      errors.streetAddress ||
      errors.zip;
  }

  render() {
    const { isInReview, onboarding } = this.props;
    const { processingSubmit, hasCompletedReview } = onboarding;
    const previousRoute = isInReview ? onboarding.previousRoute : '/patientsolutions/enroll/contact-information';
    const { disableAction } = this.state;

    const buttonProps = {
      ...this.props,
      className: 'button-submit-next',
      onSubmit: this.handleSubmit
    };

    if (disableAction && !this.hasValidations() || hasCompletedReview) {
      buttonProps.disabled = true;
    }

    return <section className='header-section'>
      <BackButton {...this.props} previousRoute={previousRoute} />
      <h1 className='section-title'>Set up your practice profile</h1>
      <p>Your practice profile includes your Practice Name, Practice Type, and Address, which patients will see along with the Doctor&apos;s name when they are searching for providers. </p>
      <form>
        <TextField
          data-test-id="practice-name-field"
          aria-required
          onBlur={this.handleNameChange}
          onKeyDown={validNameKey}
          onPaste={handleOnPasteForNameField}
          errorMsg={this.props.errors.practiceName}
          defaultValue={this.props.practice.name}
          autoFocus
          className='input-helper'
          htmlFor='practice_name'
          labelText='Practice Name'
          labelClassName='section-label'
          inputClassName='txt-field-input-medium'
          helperTextClassName='section-helper-txt'
          helperText='This name will be displayed when patients look up your practice.'
        />

        <SelectField
          data-test-id="practice-type-select"
          options={[
            { text: 'Dental', value: 'Dental' },
            { text: 'Fertility', value: 'Fertility' },
            { text: 'Hair Restoration', className: 'pill-hair', value: 'Hair Restoration' },
            { text: 'Bariatric', value: 'Bariatric' }
          ]}
          aria-required
          errorMsg={this.props.errors.practiceType}
          className='input-helper-select'
          htmlFor='practice_type'
          labelText='Practice Type'
          labelClassName='section-label'
          onKeyDown={this.handleTypeChange}
          onChange={this.handleTypeChange}
          defaultValue={this.props.practice.type}
          selectedValue={this.props.practice.type}
        />

        <Address
          actions={this.props.actions}
          entity={this.props.practice}
          errors={this.props.errors}
          smartystreetsconfig={this.props.smartystreetsconfig}
        />

        <TextField
          data-test-id="practice-legal-name-field"
          aria-required
          onBlur={this.handleLegalNameChange}
          onKeyDown={validNameKey}
          onPaste={handleOnPasteForNameField}
          errorMsg={this.props.errors.legalName}
          className='input-helper'
          htmlFor='legal_business_name'
          labelText='Legal Business Name'
          labelClassName='section-label'
          inputClassName='txt-field-input-medium'
          helperTextClassName='section-helper-txt'
          helperText='The name on your tax forms. It may differ from Practice Name.'
          defaultValue={this.props.practice.legalName}
        />


        <SelectDropDownField
          data-test-id="practice-type-select"
          aria-required
          listValues={businessTypes}
          className='input-helper'
          selectClassName='select-dropdown-field-input-medium'
          onChange={this.handleBusinessTypeChange}
          onBlur={this.handleBusinessTypeChange}
          errorMsg={this.props.errors.businessType}
          htmlFor='business_type'
          labelText='Business Type'
          labelClassName='section-label'
          helperTextClassName='section-helper-txt'
          defaultValue={this.props.practice.businessType}
        />

        <NumberField
          data-test-id="practice-tin-field"
          aria-required
          onBlur={this.handleBusinessTINBlur}
          onChange={this.handleBusinessTINChange}
          errorMsg={this.props.errors.businessTIN}
          className='input-helper'
          htmlFor='business_tin_no'
          maxLength={9}
          inputClassName='txt-field-input-small'
          placeholder='ex. 123456789'
          labelText='Business Tax Identification Number (TIN)'
          labelClassName='section-label'
          defaultValue={this.props.practice.businessTIN}
          helperTextClassName='section-helper-txt'
          helperText='The nine-digit number used as a tracking number by the U.S. Internal Revenue Service (IRS) and is required information on all tax returns filed with the IRS.'
        />

        <ActionArea {...buttonProps} />

        { processingSubmit && <Spinner processingSubmit={processingSubmit}/> }

      </form>
    </section>;
  }
}

export default PracticeInformation;

